import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiVideo, FiEdit3, FiCheck, FiX, FiSend, FiPlay, FiClock, FiGift, FiMail, FiImage, FiUsers, FiChevronDown, FiBarChart2, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface Testimonial {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  type: 'video' | 'text';
  text_content: string | null;
  video_url: string | null;
  screenshot_url: string | null;
  reward_days: number;
  reward_applied: boolean;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  is_featured: boolean;
  created_at: string;
  reviewed_at: string | null;
}

interface UserOption {
  user_id: string;
  email: string;
  name: string;
}

interface CampaignRecipient {
  id: string;
  email: string;
  name: string;
  status: 'sent' | 'failed';
  error_message: string | null;
  sent_at: string;
}

interface Campaign {
  id: string;
  sent_at: string;
  emails_sent: number;
  subject: string;
  body_template: string;
  recipients?: CampaignRecipient[];
  recipientsLoading?: boolean;
}

const TestimonialsPanel = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [videoModal, setVideoModal] = useState<string | null>(null);
  const [imageModal, setImageModal] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'testimonials' | 'campaigns'>('testimonials');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);

  // User selector states
  const [allUsers, setAllUsers] = useState<UserOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    fetchTestimonials();
    fetchAllUsers();
  }, [filter]);

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab]);

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const { data, error } = await supabase
        .from('testimonial_campaigns')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchCampaignRecipients = async (campaignId: string) => {
    if (expandedCampaign === campaignId) {
      setExpandedCampaign(null);
      return;
    }
    setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, recipientsLoading: true } : c));
    try {
      const { data, error } = await supabase
        .from('campaign_recipients')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('status', { ascending: false });
      if (error) throw error;
      setCampaigns(prev => prev.map(c =>
        c.id === campaignId ? { ...c, recipients: (data || []) as CampaignRecipient[], recipientsLoading: false } : c
      ));
      setExpandedCampaign(campaignId);
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, recipientsLoading: false } : c));
    }
  };

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('testimonials')
        .select('*')
        .order('created_at', { ascending: false });
      if (filter !== 'all') {
        query = query.eq('status', filter);
      }
      const { data, error } = await query;
      if (error) throw error;
      setTestimonials((data || []) as Testimonial[]);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, email, first_name, last_name, name')
        .not('email', 'is', null)
        .order('email');
      if (error) throw error;
      const users: UserOption[] = (data || []).map(u => ({
        user_id: u.user_id || '',
        email: u.email || '',
        name: u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.name || u.email || ''
      })).filter(u => u.email);
      setAllUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const sendCampaignEmail = async (emails?: string[]) => {
    setSending(true);
    setSendResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('send-testimonial-campaign', {
        body: { emails },
      });
      if (error) throw error;
      const count = emails?.length || data.emailsSent;
      setSendResult({
        success: true,
        message: emails
          ? `Email sent to ${count} selected user${count > 1 ? 's' : ''}!`
          : `Campaign sent to ${data.emailsSent} users!`
      });
      setSelectedUsers([]);
      setShowUserSelector(false);
      // Always refresh campaigns and switch to campaigns tab to show tracking
      await fetchCampaigns();
      setActiveTab('campaigns');
    } catch (err: any) {
      console.error('Error sending campaign:', err);
      setSendResult({
        success: false,
        message: err.message || 'Failed to send campaign'
      });
    } finally {
      setSending(false);
    }
  };

  const toggleUserSelection = (email: string) => {
    setSelectedUsers(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const filteredUsers = allUsers.filter(u =>
    u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.name.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleApprove = async (testimonial: Testimonial) => {
    setProcessing(testimonial.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updateError } = await supabase
        .from('testimonials')
        .update({
          status: 'approved',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', testimonial.id);
      if (updateError) throw updateError;
      if (!testimonial.reward_applied) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('paid_until, subscription_plan_id')
          .eq('user_id', testimonial.user_id)
          .maybeSingle();
        const currentPaidUntil = profile?.paid_until ? new Date(profile.paid_until) : new Date();
        const newPaidUntil = new Date(Math.max(currentPaidUntil.getTime(), Date.now()));
        newPaidUntil.setDate(newPaidUntil.getDate() + testimonial.reward_days);
        await supabase
          .from('user_profiles')
          .update({
            paid_until: newPaidUntil.toISOString(),
            subscription_plan_id: 'institutional_ai',
            subscription_status: 'active',
            billing_status: 'active',
          })
          .eq('user_id', testimonial.user_id);
        await supabase
          .from('testimonials')
          .update({ reward_applied: true, reward_applied_at: new Date().toISOString() })
          .eq('id', testimonial.id);
      }
      fetchTestimonials();
    } catch (err) {
      console.error('Error approving:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (testimonial: Testimonial) => {
    const notes = prompt('Reason for rejection (optional):');
    setProcessing(testimonial.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('testimonials')
        .update({
          status: 'rejected',
          admin_notes: notes,
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', testimonial.id);
      if (error) throw error;
      fetchTestimonials();
    } catch (err) {
      console.error('Error rejecting:', err);
    } finally {
      setProcessing(null);
    }
  };

  const toggleFeatured = async (testimonial: Testimonial) => {
    try {
      await supabase
        .from('testimonials')
        .update({ is_featured: !testimonial.is_featured })
        .eq('id', testimonial.id);
      fetchTestimonials();
    } catch (err) {
      console.error('Error toggling featured:', err);
    }
  };

  const pendingCount = testimonials.filter(t => t.status === 'pending').length;

  // Emails that failed due to rate limit on last campaign
  const failedEmails = [
    "diaslawrie@gmail.com", "remisoltys@gmail.com", "jp@snap-chart.ai",
    "justcascante@gmail.com", "jadidirtakhoiri22@gmail.com", "galleallen010@gmail.com",
    "ste631606@gmail.com", "probroinvestments-snap@protonmail.com",
    "echeverridahiana54@gmail.com", "mzeeshansheikhone@gmail.com",
    "t.dance@setonhill.edu", "trendykingsuccess@gmail.com",
    "jess@digitaltrvst.com", "comptepourdesvideocool@gmail.com",
    "nicolas.carrier24@gmail.com", "semerz19@gmail.com"
  ];

  const getActionsInfo = (t: Testimonial) => {
    let count = 0;
    const actions: string[] = [];
    if (t.video_url) { count++; actions.push('Video'); }
    if (t.text_content && t.text_content.length >= 50) { count++; actions.push('Text'); }
    if (t.screenshot_url) { count++; actions.push('Screenshot'); }
    return { count, actions };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FiGift className="text-purple-400" />
            Testimonials Management
            {pendingCount > 0 && (
              <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Review testimonials and manage email campaigns
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">

          {/* Select Users Button */}
          <div className="relative">
            <button
              onClick={() => setShowUserSelector(!showUserSelector)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium text-gray-300 flex items-center gap-2 transition-colors"
            >
              <FiUsers className="w-4 h-4" />
              Select Users
              {selectedUsers.length > 0 && (
                <span className="px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                  {selectedUsers.length}
                </span>
              )}
              <FiChevronDown className={`w-4 h-4 transition-transform ${showUserSelector ? 'rotate-180' : ''}`} />
            </button>

            {showUserSelector && (
              <div className="absolute top-full right-0 mt-2 w-80 bg-gray-800 border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Search */}
                <div className="p-3 border-b border-white/10">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  />
                </div>
                {/* Select All row */}
                <div className="border-b border-white/10">
                  <label className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedUsers.includes(u.email))}
                      onChange={() => {
                        const allSelected = filteredUsers.every(u => selectedUsers.includes(u.email));
                        if (allSelected) {
                          setSelectedUsers(prev => prev.filter(e => !filteredUsers.map(u => u.email).includes(e)));
                        } else {
                          setSelectedUsers(prev => [...new Set([...prev, ...filteredUsers.map(u => u.email)])]);
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Select All ({filteredUsers.length})
                    </span>
                  </label>
                </div>
                {/* User list */}
                <div className="max-h-52 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="text-gray-400 text-sm p-4 text-center">No users found</p>
                  ) : (
                    filteredUsers.map(user => (
                      <label key={user.email} className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.email)}
                          onChange={() => toggleUserSelection(user.email)}
                          className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{user.name}</p>
                          <p className="text-gray-400 text-xs truncate">{user.email}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {/* Footer: always visible */}
                <div className="p-3 border-t border-white/10 flex gap-2">
                  <button
                    onClick={() => setSelectedUsers([])}
                    disabled={selectedUsers.length === 0}
                    className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-40 rounded-lg text-sm text-gray-300 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => {
                      if (selectedUsers.length === 0) return;
                      sendCampaignEmail(selectedUsers);
                      setShowUserSelector(false);
                    }}
                    disabled={sending || selectedUsers.length === 0}
                    className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 rounded-lg text-sm text-white font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><FiSend className="w-4 h-4" /> Send ({selectedUsers.length})</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Result */}
      <AnimatePresence>
        {sendResult && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-3 rounded-lg text-sm ${sendResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
          >
            {sendResult.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-0">
        <button
          onClick={() => setActiveTab('testimonials')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'testimonials'
              ? 'border-purple-500 text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <FiGift className="inline mr-2" />
          Testimonials
        </button>
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'campaigns'
              ? 'border-purple-500 text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <FiBarChart2 className="inline mr-2" />
          Campaigns
        </button>
      </div>

      {/* TESTIMONIALS TAB */}
      {activeTab === 'testimonials' && (
        <>
          {/* Filter Tabs */}
          <div className="flex gap-2">
            {(['pending', 'approved', 'rejected', 'all'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === tab
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No testimonials found</div>
          ) : (
            <div className="space-y-4">
              {testimonials.map((testimonial) => {
                const { count: actionsCount, actions } = getActionsInfo(testimonial);
                return (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-white/5 border border-white/10 rounded-xl"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            testimonial.type === 'video' ? 'bg-purple-600/20 text-purple-400' : 'bg-cyan-600/20 text-cyan-400'
                          }`}>
                            {testimonial.type === 'video' ? <FiVideo /> : <FiEdit3 />}
                          </div>
                          <div>
                            <p className="font-semibold text-white">{testimonial.user_name}</p>
                            <p className="text-sm text-gray-400">{testimonial.user_email}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            testimonial.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            testimonial.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {testimonial.status}
                          </span>
                          {testimonial.is_featured && (
                            <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-full">Featured</span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            actionsCount === 3 ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {actionsCount}/3: {actions.join(', ')}
                          </span>
                        </div>
                        <div className="space-y-2 mt-3">
                          {testimonial.text_content && (
                            <p className="text-gray-300 text-sm line-clamp-3">"{testimonial.text_content}"</p>
                          )}
                          <div className="flex flex-wrap gap-3">
                            {testimonial.video_url && (
                              <button
                                onClick={() => setVideoModal(testimonial.video_url)}
                                className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm"
                              >
                                <FiPlay /> Watch Video
                              </button>
                            )}
                            {testimonial.screenshot_url && (
                              <button
                                onClick={() => setImageModal(testimonial.screenshot_url)}
                                className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-sm"
                              >
                                <FiImage /> View Screenshot
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <FiClock />{new Date(testimonial.created_at).toLocaleDateString()}
                          </span>
                          <span className={`flex items-center gap-1 ${testimonial.reward_days === 60 ? 'text-green-400' : 'text-cyan-400'}`}>
                            <FiGift />{testimonial.reward_days} days
                            {testimonial.reward_applied && <FiCheck className="text-green-400" />}
                          </span>
                        </div>
                      </div>
                      {testimonial.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(testimonial)}
                            disabled={processing === testimonial.id}
                            className="p-2 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded-lg transition-colors"
                            title="Approve"
                          >
                            {processing === testimonial.id ? (
                              <div className="w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FiCheck className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleReject(testimonial)}
                            disabled={processing === testimonial.id}
                            className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                            title="Reject"
                          >
                            <FiX className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {testimonial.status === 'approved' && (
                        <button
                          onClick={() => toggleFeatured(testimonial)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                            testimonial.is_featured ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                          }`}
                        >
                          {testimonial.is_featured ? 'Featured' : 'Feature'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* CAMPAIGNS TAB */}
      {activeTab === 'campaigns' && (
        <div className="space-y-3">
          {/* Header summary */}
          {!campaignsLoading && campaigns.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{campaigns.length}</p>
                <p className="text-xs text-gray-400 mt-1">Campaigns sent</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">{campaigns.reduce((s, c) => s + c.emails_sent, 0)}</p>
                <p className="text-xs text-gray-400 mt-1">Total emails sent</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-400">
                  {campaigns.reduce((s, c) => s + (c.recipients?.filter(r => r.status === 'sent').length || 0), 0)}
                </p>
                <p className="text-xs text-gray-400 mt-1">Tracked deliveries</p>
              </div>
            </div>
          )}

          {campaignsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FiMail className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No campaigns yet</p>
              <p className="text-sm mt-1">Use the Send Campaign button to start.</p>
            </div>
          ) : (
            campaigns.map((campaign, idx) => {
              const recipients = campaign.recipients || [];
              const sentCount = recipients.filter(r => r.status === 'sent').length;
              const failedCount = recipients.filter(r => r.status === 'failed').length;
              const isExpanded = expandedCampaign === campaign.id;
              const hasRecipientData = recipients.length > 0;
              const deliveryRate = hasRecipientData ? Math.round((sentCount / recipients.length) * 100) : null;

              return (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="bg-[#0f1623] border border-white/8 rounded-xl overflow-hidden"
                >
                  {/* Campaign row */}
                  <button
                    onClick={() => fetchCampaignRecipients(campaign.id)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/4 transition-colors text-left group"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center text-purple-400 flex-shrink-0">
                        <FiMail className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-white text-sm truncate">{campaign.subject}</p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {new Date(campaign.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <span className="px-2.5 py-1 bg-blue-500/15 text-blue-400 text-xs rounded-full font-semibold">
                        {campaign.emails_sent} sent
                      </span>
                      {deliveryRate !== null && (
                        <span className={`px-2.5 py-1 text-xs rounded-full font-semibold ${
                          deliveryRate >= 90 ? 'bg-green-500/15 text-green-400' :
                          deliveryRate >= 60 ? 'bg-yellow-500/15 text-yellow-400' :
                          'bg-red-500/15 text-red-400'
                        }`}>
                          {deliveryRate}% delivered
                        </span>
                      )}
                      {failedCount > 0 && (
                        <span className="px-2.5 py-1 bg-red-500/15 text-red-400 text-xs rounded-full font-semibold">
                          {failedCount} failed
                        </span>
                      )}
                      {campaign.recipientsLoading ? (
                        <div className="w-4 h-4 border border-purple-400 border-t-transparent rounded-full animate-spin ml-1" />
                      ) : (
                        <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-1 ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-white/8">
                          {campaign.recipientsLoading ? (
                            <div className="flex items-center justify-center py-10">
                              <div className="w-6 h-6 border border-purple-400 border-t-transparent rounded-full animate-spin" />
                            </div>
                          ) : !hasRecipientData ? (
                            <div className="px-5 py-6">
                              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/8">
                                <FiAlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-gray-300 font-medium">No recipient tracking for this campaign</p>
                                  <p className="text-xs text-gray-500 mt-0.5">This campaign was sent before the detailed tracking system was enabled.</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white/5 rounded-lg p-3 text-center border border-white/8">
                                  <p className="text-xl font-bold text-blue-400">{campaign.emails_sent}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Emails sent</p>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 text-center border border-white/8">
                                  <p className="text-xl font-bold text-gray-400">—</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Detail unavailable</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Stats row */}
                              <div className="grid grid-cols-3 divide-x divide-white/8">
                                <div className="px-5 py-4 text-center">
                                  <p className="text-xl font-bold text-blue-400">{recipients.length}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Targeted</p>
                                </div>
                                <div className="px-5 py-4 text-center">
                                  <p className="text-xl font-bold text-green-400">{sentCount}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Delivered</p>
                                </div>
                                <div className="px-5 py-4 text-center">
                                  <p className="text-xl font-bold text-red-400">{failedCount}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">Failed</p>
                                </div>
                              </div>

                              {/* Recipient list */}
                              <div className="border-t border-white/8 max-h-72 overflow-y-auto">
                                {recipients.map((r) => (
                                  <div
                                    key={r.id}
                                    className="flex items-center justify-between px-5 py-2.5 hover:bg-white/3 border-b border-white/5 last:border-0"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${r.status === 'sent' ? 'bg-green-400' : 'bg-red-400'}`} />
                                      <div className="min-w-0">
                                        <p className="text-white text-sm font-medium truncate">{r.name || r.email}</p>
                                        {r.name && <p className="text-gray-500 text-xs truncate">{r.email}</p>}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                        r.status === 'sent' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
                                      }`}>
                                        {r.status === 'sent' ? '✓ Delivered' : '✗ Failed'}
                                      </span>
                                      {r.error_message && (
                                        <span className="text-xs text-gray-600 max-w-28 truncate" title={r.error_message}>
                                          {r.error_message.includes('429') ? 'Rate limited' : r.error_message}
                                        </span>
                                      )}
                                      <span className="text-xs text-gray-600 tabular-nums">
                                        {new Date(r.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Failed count info only */}
                              {failedCount > 0 && (
                                <div className="px-5 py-3 border-t border-white/8">
                                  <p className="text-xs text-gray-500">{failedCount} email(s) not delivered</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })
          )}
        </div>
      )}

      {/* Video Modal */}
      {videoModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setVideoModal(null)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <video src={videoModal} controls autoPlay className="w-full rounded-xl" />
            <button
              onClick={() => setVideoModal(null)}
              className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white mx-auto block"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModal && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setImageModal(null)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={imageModal} alt="Trading Screenshot" className="w-full max-h-[80vh] object-contain rounded-xl" />
            <button
              onClick={() => setImageModal(null)}
              className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white mx-auto block"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsPanel;
